import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import CardList from "../ui/CardList.jsx";
import { phoneAlert } from "../ui/dialogs.jsx";
import { phoneUi } from "../ui/phoneUi";
import {
  COUNT_CORRECTION_REASON,
  TRANSACTION_REASONS,
  loadConsumable,
  loadConsumableTransactions,
  loadConsumables,
  loadReorderAlerts,
  matchesConsumableSearch,
  quantityFromCount,
  recordConsumableTransaction,
  reorderStatus,
} from "../../features/consumables/consumables";
import { stamp } from "../../utils/tmDefects";
import "../mobile.css";

// The phone Consumables page (plan Phase 1): find an item by search or by scanning its bin label
// (the label's QR opens /consumables?id=N, which shows that item), see what is on hand and whether
// it needs ordering, record use / stock added / a count, and its recent transactions. Editing,
// suppliers, laser specs, gas sessions, analytics and labels stay on the desktop page.
// Layout only: features/consumables/consumables.js is the desktop page's own code.

const RECENT = 15;

function itemId(searchParams) {
  const raw = searchParams.get("id");
  if (!raw || !/^\d+$/.test(raw)) return null;
  const n = Number(raw);
  return n > 0 ? n : null;
}

const qtyClass = (n) => (Number(n) < 0 ? "text-danger fw-bold" : "");

export default function ConsumablesScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const id = itemId(searchParams);
  if (id) return <ConsumableItem key={id} id={id} onAll={() => setSearchParams({})} />;
  return <ConsumableList onOpen={(c) => setSearchParams({ id: String(c.consumable_id) })} />;
}

// ---------------------------------------------------------------------------------------------
// The list: search, "needs ordering" filter, one card per item.

function ConsumableList({ onOpen }) {
  const [items, setItems] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [term, setTerm] = useState("");
  const [lowOnly, setLowOnly] = useState(false);

  useEffect(() => {
    let live = true;
    loadConsumables()
      .then((data) => { if (live) setItems(Array.isArray(data) ? data : []); })
      .catch((err) => { if (live) setError(err.message || "Couldn't load the consumables."); })
      .finally(() => { if (live) setLoading(false); });
    loadReorderAlerts()
      .then((data) => { if (live) setAlerts(Array.isArray(data) ? data : []); })
      .catch(() => { /* the list still works without reorder flags */ });
    return () => { live = false; };
  }, []);

  const alertById = useMemo(() => new Map(alerts.map((a) => [a.consumable_id, a])), [alerts]);
  const shown = useMemo(
    () => items.filter((c) => matchesConsumableSearch(c, term) && (!lowOnly || alertById.has(c.consumable_id))),
    [items, term, lowOnly, alertById],
  );

  return (
    <div className="jq-screen" data-testid="phone-consumables">
      <div className="d-flex gap-2 mb-2" role="group" aria-label="Show">
        <button type="button" className={`btn btn-sm ${lowOnly ? "btn-outline-secondary" : "btn-secondary"}`} aria-pressed={!lowOnly} onClick={() => setLowOnly(false)}>
          All
        </button>
        <button type="button" className={`btn btn-sm ${lowOnly ? "btn-danger" : "btn-outline-danger"}`} aria-pressed={lowOnly} onClick={() => setLowOnly(true)}>
          Needs ordering ({alerts.length})
        </button>
      </div>
      <CardList
        items={shown}
        getKey={(c) => c.consumable_id}
        onItemClick={onOpen}
        search={{ value: term, onChange: setTerm, placeholder: "Search SKU, description, category" }}
        renderCard={(c) => {
          const alert = alertById.get(c.consumable_id);
          return (
            <div>
              <div className="d-flex align-items-center gap-2">
                <span className="fw-bold">{c.product_id || "???"}</span>
                {c.consumable_type === "component" && c.current_revision_code && <span className="jq-tag">Rev {c.current_revision_code}</span>}
                {alert && <span className="jq-tag jq-tag-block">Order {alert.recommended_qty}</span>}
              </div>
              <div className="jq-muted">{c.description}</div>
              <div className="jq-muted">
                On hand <span className={qtyClass(c.stock_quantity)}>{Number(c.stock_quantity) || 0}</span>
                {Number(c.reorder_point) > 0 ? ` · reorder at ${c.reorder_point}` : ""}
                {c.category ? ` · ${c.category}` : ""}
              </div>
            </div>
          );
        }}
        loading={loading}
        error={error}
        empty={lowOnly ? "Nothing needs ordering right now." : "No items match."}
        testId="phone-consumables-list"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// One item: on hand + reorder status, record a transaction, recent transactions.

function ConsumableItem({ id, onAll }) {
  const [item, setItem] = useState(null);
  const [error, setError] = useState(null);
  const [transactions, setTransactions] = useState(null);
  const [alertRow, setAlertRow] = useState(null);
  const [alertsRead, setAlertsRead] = useState(false);
  const [reason, setReason] = useState("");
  const [count, setCount] = useState("");
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const live = useRef(true);

  const readAlerts = useCallback(async () => {
    try {
      const rows = await loadReorderAlerts();
      if (!live.current) return;
      setAlertRow((Array.isArray(rows) ? rows : []).find((r) => r.consumable_id === id) || null);
      setAlertsRead(true);
    } catch (err) {
      if (live.current) setAlertsRead(false);
    }
  }, [id]);

  const readTransactions = useCallback(async () => {
    const rows = await loadConsumableTransactions(id);
    if (live.current && rows) setTransactions(rows);
  }, [id]);

  useEffect(() => {
    live.current = true;
    loadConsumable(id)
      .then((data) => { if (live.current) setItem(data); })
      .catch((err) => { if (live.current) setError(err.message || "Couldn't load this item."); });
    readTransactions();
    readAlerts();
    return () => { live.current = false; };
  }, [id, readTransactions, readAlerts]);

  const picked = TRANSACTION_REASONS.find((r) => r.value === reason);
  const isCount = reason === COUNT_CORRECTION_REASON;

  async function record() {
    const { quantity, problem } = quantityFromCount(reason, count);
    if (problem) {
      await phoneAlert(problem);
      return;
    }
    setBusy(true);
    const { data } = await recordConsumableTransaction({ consumableId: id, reason, quantity, ui: phoneUi });
    setBusy(false);
    if (!data || !live.current) return;
    setItem((prev) => (prev ? { ...prev, stock_quantity: data.new_quantity } : prev));
    setReason("");
    setCount("");
    readTransactions();
    readAlerts();
  }

  if (error) {
    return (
      <div className="jq-screen" data-testid="phone-consumable">
        <div className="jq-note jq-note-danger" role="alert">{error}</div>
        <button type="button" className="btn btn-outline-primary btn-lg w-100" onClick={onAll}>All consumables</button>
      </div>
    );
  }
  if (!item) {
    return (
      <div className="jq-list-state" role="status" data-testid="phone-consumable">
        <span className="spinner-border spinner-border-sm" aria-hidden="true" />
      </div>
    );
  }

  const status = reorderStatus(item, alertRow, alertsRead);
  const rows = transactions || [];
  const visible = showAll ? rows : rows.slice(0, RECENT);

  return (
    <div className="jq-screen" data-testid="phone-consumable">
      <div className="d-flex gap-3 align-items-start mb-2">
        {item.image_url ? <img className="jq-part-img" src={item.image_url} alt="" /> : null}
        <div style={{ minWidth: 0 }}>
          <div className="jq-qcard-title">
            {item.product_id}
            {item.consumable_type === "component" && item.current_revision_code && <span className="jq-tag ms-2">Rev {item.current_revision_code}</span>}
          </div>
          <div>{item.description}</div>
          {item.category && <div className="jq-muted">{item.category}</div>}
        </div>
      </div>

      <dl className="jq-kv" data-testid="phone-consumable-stock">
        <div>
          <dt>On hand</dt>
          <dd className={qtyClass(item.stock_quantity)}>{Number(item.stock_quantity) || 0}</dd>
        </div>
        <div>
          <dt>Reorder point</dt>
          <dd>{Number(item.reorder_point) > 0 ? item.reorder_point : "None"}</dd>
        </div>
      </dl>
      <div className={`jq-note ${status.tone === "danger" ? "jq-note-danger" : "jq-note-info"}`} data-testid="phone-consumable-status">
        <strong>{status.text}</strong>
        {status.detail ? ` · ${status.detail}` : ""}
      </div>

      <h2 className="jq-section-title mt-3">Record</h2>
      <div className="jq-filter-field">
        <label htmlFor="phone-tx-reason">Reason</label>
        <select id="phone-tx-reason" className="form-select form-select-lg" value={reason} onChange={(e) => setReason(e.target.value)}>
          <option value="">-- Select Reason --</option>
          {TRANSACTION_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      </div>
      <div className="jq-filter-field">
        <label htmlFor="phone-tx-count">{isCount ? "Counted on the shelf" : "How many"}</label>
        <input
          id="phone-tx-count"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          className="form-control form-control-lg"
          value={count}
          placeholder={isCount ? String(Number(item.stock_quantity) || 0) : ""}
          onChange={(e) => setCount(e.target.value)}
        />
        {picked && (
          <div className="jq-muted mt-1">
            {picked.direction === "in" && "Adds to stock."}
            {picked.direction === "out" && "Takes out of stock."}
            {picked.direction === "count" && "Sets stock to the number counted (not counted as use)."}
          </div>
        )}
      </div>
      <button type="button" className="btn btn-primary btn-lg w-100" disabled={busy || !reason} onClick={record}>
        {busy ? "Saving…" : "Record"}
      </button>

      <h2 className="jq-section-title mt-4">Recent transactions</h2>
      {transactions === null ? (
        <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /></div>
      ) : rows.length === 0 ? (
        <div className="jq-list-state">No transactions yet.</div>
      ) : (
        <div data-testid="phone-consumable-history">
          {visible.map((t) => (
            <div key={t.transaction_id} className="jq-task-line">
              <div className="jq-task-line-main">
                <div>
                  {t.reason}
                  {t.is_count_correction && <span className="jq-tag ms-2" title="Physical-count correction: not counted as usage or a swap">count</span>}
                </div>
                <div className="jq-muted">
                  {stamp(t.created_at)}
                  {t.user_name ? ` · ${t.user_name}` : ""}
                </div>
              </div>
              <span className={`fw-bold ${Number(t.delta) < 0 ? "text-danger" : "text-success"}`}>
                {Number(t.delta) > 0 ? `+${t.delta}` : t.delta}
              </span>
            </div>
          ))}
          {!showAll && rows.length > RECENT && (
            <button type="button" className="btn btn-outline-secondary jq-show-more mt-2" onClick={() => setShowAll(true)}>
              Show all ({rows.length})
            </button>
          )}
        </div>
      )}
      <button type="button" className="btn btn-link w-100 mt-3" onClick={onAll}>All consumables</button>
    </div>
  );
}
